// Trailing punctuation that's almost never part of a URL — the regex
// happily captures it (e.g. `https://ano.chat)` inside a markdown link)
// and we need it stripped BEFORE the dedup pass below; otherwise the
// hostname parsed from the trailing-punct-tainted URL looks different
// from the bare-domain match's hostname, and the bare gets pushed even
// though it points to the same target (#1792).
const TRAILING_PUNCT_RE = /[.,;:!?)]+$/;

function trimTrailingPunct(url: string): string {
  return url.replace(TRAILING_PUNCT_RE, "");
}

/**
 * Extract URLs from message content.
 * Matches http/https URLs and bare domains (e.g., github.com).
 *
 * The returned list is deduped by exact-match string so a single
 * surface URL never produces multiple preview cards — the historical
 * shape produced two rows from `[ano.chat](https://ano.chat)` because
 * the markdown link-text re-matched the bare-domain regex
 * (#1792).
 */
export function extractUrls(content: string): string[] {
  const urls: string[] = [];

  // Strip markdown images ![alt](url) so we don't unfurl embedded images/GIFs.
  // Post-Phase-2+3 content no longer carries <!--card:--> markers (card kinds
  // put their data in `payload`, not `content`), so the legacy marker strip
  // has been removed.
  const stripped = content.replace(/!\[[^\]]*\]\([^)]*\)/g, "");

  // Match URLs with explicit protocol (http:// or https://). Trim
  // trailing punctuation up front so the hostname extracted for the
  // dedup set matches what the final URL will be.
  const protocolRegex = /https?:\/\/[^\s<>"\]]+/gi;
  const protocolMatches = (stripped.match(protocolRegex) || []).map(
    trimTrailingPunct,
  );
  urls.push(...protocolMatches);

  // Match bare domains (e.g., github.com, docs.google.com, example.co.uk)
  // Must have at least one dot and a valid TLD-like ending
  const bareDomainRegex =
    /(?<![/@\w.-])([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[^\s<>"\]]*)?/gi;
  const bareDomainMatches = stripped.match(bareDomainRegex) || [];

  // Filter out bare domains that were already captured with protocol
  const protocolHosts = new Set(
    protocolMatches.map((url) => {
      try {
        return new URL(url).hostname.toLowerCase();
      } catch {
        return "";
      }
    }),
  );

  for (const domain of bareDomainMatches) {
    const hostname = domain.split("/")[0].toLowerCase();
    if (!protocolHosts.has(hostname)) {
      urls.push("https://" + trimTrailingPunct(domain));
    }
  }

  // Final dedup so the link-preview processor never inserts two rows
  // pointing at the same target. Identical-string match is sufficient
  // here — case-sensitivity / trailing-slash variants are intentionally
  // treated as distinct since they may resolve to different pages.
  return Array.from(new Set(urls));
}
